// src/mocks/handlers/funding/revenuesHandler.ts

import { rest } from 'msw';
import revenues from '@/mocks/data/fundingRevenueData.json';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import { IRevenue, TRevenueSortBy } from '@/domains/funding/revenues.types';
import { API_DEFAULT_PAGE_SIZE } from '@/services/api/api.constants';
import { matchesDateRange } from '@/mocks/lib/matchesDateRange';
import { matchesAgencyFilter } from '@/mocks/lib/matchesAgencyFilter';
import { PaginatedResponse } from '@/shared/types/api';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';

/* eslint-disable no-console */

export const fundingRevenuesHandler = rest.get<PaginatedResponse<IRevenue>>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.FUNDING_REVENUES}`,
  (req, res, ctx) => {
    const token = req.headers.get('Authorization');
    if (!token) {
      return res(ctx.status(500), ctx.json({ detail: 'Something went wrong' }));
    }

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const { customer_uuid } = req.params as { customer_uuid: string };
    const group = revenues.find((g) => g.uuid === customer_uuid);
    if (!group) {
      return res(
        ctx.status(200),
        ctx.json({ total: 0, page: 1, pages: 1, results: [] }),
      );
    }

    const url = new URL(req.url);
    const params = url.searchParams;

    console.log('Funding Revenues Handler', {
      url: req.url,
      params: Object.fromEntries(params.entries()),
      headers: Object.fromEntries(req.headers.entries()),
    });

    const currencies = params.getAll('currencies');
    const sources = params.getAll('sources');
    const agencies = params.getAll('agencies');
    const campaigns = params.getAll('campaigns');
    const event_date_start = params.get('event_date_start');
    const event_date_end = params.get('event_date_end');
    const expected_repayment_date_start = params.get(
      'expected_repayment_date_start',
    );
    const expected_repayment_date_end = params.get(
      'expected_repayment_date_end',
    );

    const ordering = params.get('ordering') || '';
    const page = parseInt(params.get('page') || '1', 10);
    const page_size = parseInt(
      params.get('page_size') || `${API_DEFAULT_PAGE_SIZE}`,
      10,
    );
    const search = params.get('search') || '';

    // 1. Filter
    let filtered = (group.results as IRevenue[]).filter((item) => {
      const matchCurrency =
        currencies.length > 0 ? currencies.includes(item.currency) : true;
      const matchSource =
        sources.length > 0 ? sources.includes(item.source_name) : true;
      const matchAgency = matchesAgencyFilter(item.agency_name, agencies);
      const matchCampaign =
        campaigns.length > 0
          ? Boolean(
              item.campaign_uuid && campaigns.includes(item.campaign_uuid),
            )
          : true;
      const matchEventDateRange =
        event_date_start && event_date_end
          ? matchesDateRange(item.event_date, event_date_start, event_date_end)
          : true;
      const matchExpectedRepaymentDateRange =
        expected_repayment_date_start && expected_repayment_date_end
          ? matchesDateRange(
              item.expected_repayment_date,
              expected_repayment_date_start,
              expected_repayment_date_end,
            )
          : true;

      return (
        matchCurrency &&
        matchSource &&
        matchAgency &&
        matchCampaign &&
        matchEventDateRange &&
        matchExpectedRepaymentDateRange
      );
    });

    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter((item) => {
        const haystack = [
          item.advance_id,
          item.source_name,
          item.agency_name,
          item.campaign_name,
          item.publisher_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(lower);
      });
    }

    // 2. Sort
    if (ordering) {
      const isDesc = ordering.startsWith('-');
      const key = (isDesc ? ordering.slice(1) : ordering) as TRevenueSortBy;

      filtered = filtered.sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return isDesc
            ? bValue.localeCompare(aValue)
            : aValue.localeCompare(bValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return isDesc ? bValue - aValue : aValue - bValue;
        }

        return 0;
      });
    }

    // 3. Paginate
    const total = filtered.length;
    const pages = Math.ceil(total / page_size);
    const start = Math.max(0, (page - 1) * page_size);
    const end = Math.min(start + page_size, total);
    const results = filtered.slice(start, end);

    // 4. Response
    return res(
      ctx.status(200),
      ctx.json({
        total,
        page,
        pages,
        results,
      }),
    );
  },
);
